import React, { useState } from 'react';
import classNames from 'classnames';
import { scroller } from 'react-scroll';
import { useApolloClient, FetchResult } from '@apollo/client';
import { TransitionGroup, CSSTransition } from 'react-transition-group';

import Button, { BUTTON_SIZES, BUTTON_TYPES } from '@buyside/components/presentation/Button/button';
import CommentBodyRenderer from '@buyside/components/presentation/CommentBodyRenderer/commentBodyRenderer';
import ShowPageCommentContainer, {
  ShowPageCommentType
} from '@buyside/components/container/ShowPageComments/ShowPageCommentContainer';
import ShowPageReplyForm from '@buyside/components/presentation/ShowPageComments/ShowPageReplyForm/showPageReplyForm';
import WhitePaperCommentForm from '@buyside/components/presentation/WhitePaper/Comments/WhitePaperCommentForm';
import CommentHeader from '@buyside/components/presentation/Comment/CommentHeader/commentHeader';
import CommentAttachments from '@buyside/components/presentation/Comment/CommentAttachments/commentAttachments';
import CommentActions from '@buyside/components/presentation/Comment/CommentActions/commentActions';

import { useTruncatedComment } from '@buyside/util/hooks';

import {
  IdeaCoreQuery,
  Maybe,
  Mentionable,
  LikeCommentMutation
} from '@interfaces/graphql-typings';

const IdeaCore = require('buyside/graphql/queries/ideaShowPage/IdeaCore');

import styles from './showPageCommentStyles.scss';

export type ShowPageCommentPropTypes = {
  allComments: ShowPageCommentType[];
  authorTag?: string;
  authorId: Maybe<string>;
  authorName: Maybe<string>;
  canCommentAnonymously: boolean;
  canViewUserDetails: boolean;
  canWriteComments: boolean;
  childComments: ShowPageCommentType[];
  comment: ShowPageCommentType;
  commentableId: string;
  commentableType: string;
  currentUserId?: string;
  handleLike: () => Promise<FetchResult<LikeCommentMutation>>;
  highlightedCommentId?: Maybe<string>;
  isAdmin: boolean;
  isAllocator: boolean;
  isAuthor: boolean;
  isEmployed: boolean;
  isIdeaAnonymous: boolean;
  isNonBuyside: boolean;
  likeLoading: boolean;
  mentionables: Maybe<Mentionable>[];
  profileImageUrl: Maybe<string>;
};

const ShowPageComment: React.FC<ShowPageCommentPropTypes> = ({
  allComments,
  authorTag,
  authorId,
  authorName,
  canCommentAnonymously,
  canViewUserDetails,
  canWriteComments,
  childComments,
  comment,
  commentableId,
  commentableType,
  currentUserId,
  handleLike,
  highlightedCommentId,
  isAdmin,
  isAllocator,
  isAuthor,
  isEmployed,
  isIdeaAnonymous,
  isNonBuyside,
  likeLoading,
  mentionables,
  profileImageUrl
}): React.ReactElement | null => {
  const [showChildComments, setShowChildComments] = useState(true);
  const [showReplyForm, setShowReplyForm] = useState(false);

  const client = useApolloClient();
  const cachedIdea = client.readQuery<IdeaCoreQuery>({
    query: IdeaCore,
    variables: { ideaId: commentableId }
  })?.idea;

  const idTag = `comment-${comment?.id}-top`;

  const {
    handleToggleTruncation,
    truncatable,
    truncated,
    truncatedContentRef
  } = useTruncatedComment(idTag, comment?.body);

  if (!comment) return null;

  const {
    body,
    responseRequested,
    user,
    anonUser,
    likeCount,
    currentUserHasLiked,
    createdAt,
    commentAttachments
  } = comment;

  const isAuthorComment = authorId === user?.id;

  const showUserInfo =
    canViewUserDetails || currentUserId === user?.id || (isNonBuyside && isAuthorComment);

  if (highlightedCommentId === comment?.id)
    scroller.scrollTo(idTag, {
      duration: 1000,
      delay: 0,
      smooth: 'easeInOutQuint',
      offset: -(65 + 45 + 15) // -(top navigation height + page navigator height + some padding)
    });

  return (
    <div
      className={classNames(styles.commentContainer, {
        [styles.childComment]: Boolean(comment?.parentId)
      })}
    >
      <div className={styles.comment} data-heap-segment="show-page-comment" id={idTag}>
        <CommentHeader
          anonUser={anonUser}
          authorName={authorName}
          createdAt={createdAt}
          isAdmin={isAdmin}
          isAuthor={isAuthor}
          isAuthorComment={isAuthorComment}
          isNonBuyside={isNonBuyside}
          responseRequested={Boolean(responseRequested)}
          showUserInfo={showUserInfo}
          user={user}
        />
        <div className={styles.commentBody}>
          <div
            className={classNames(
              {
                [styles.truncated]: truncated,
                [styles.blur]:
                  commentableType === 'Idea' && !cachedIdea?.canCurrentUserAccessIdeaComments
              },
              styles.unselectable
            )}
            ref={truncatedContentRef}
          >
            <CommentBodyRenderer body={body} />
          </div>
          {truncatable ? (
            <Button
              onClick={handleToggleTruncation}
              size={BUTTON_SIZES.SMALL}
              text={truncated ? 'Read More' : 'Read Less'}
              type={BUTTON_TYPES.LINK}
            />
          ) : null}
        </div>
        <CommentAttachments commentAttachments={commentAttachments} />
        <CommentActions
          canWriteComments={canWriteComments}
          commentId={comment.id}
          currentUserHasLiked={currentUserHasLiked}
          hasChildComments={Boolean(childComments?.length)}
          likeComment={handleLike}
          likeCount={likeCount}
          likeLoading={likeLoading}
          showChildComments={showChildComments}
          toggleReply={(): void => setShowReplyForm(!showReplyForm)}
          toggleShowChildComments={(): void => setShowChildComments(!showChildComments)}
        />
        {showReplyForm && commentableType === 'WhitePaper' && (
          <WhitePaperCommentForm
            whitePaperId={commentableId}
            canCommentAnonymously={canCommentAnonymously}
            currentUserId={currentUserId || ''}
            isAuthor={isAuthor}
            isReplying
            parentId={comment.id}
            closeFormOpenThread={(): void => {
              setShowReplyForm(false);
              setShowChildComments(true);
            }}
            closeForm={(): void => setShowReplyForm(false)}
          />
        )}
        {showReplyForm && commentableType === 'Idea' && (
          <ShowPageReplyForm
            canCommentAnonymously={canCommentAnonymously}
            isAllocator={isAllocator}
            isAuthor={isAuthor}
            ideaId={commentableId}
            isIdeaAnonymous={isIdeaAnonymous}
            parentId={comment.id}
            mentionables={mentionables}
            profileImageUrl={profileImageUrl}
            closeFormOpenThread={(): void => {
              setShowReplyForm(false);
              setShowChildComments(true);
            }}
            closeForm={(): void => setShowReplyForm(false)}
          />
        )}
      </div>
      <div className={styles.childComments}>
        {showChildComments && (
          <TransitionGroup>
            {childComments.map(c => (
              <CSSTransition key={`comment-${c?.id}`} classNames="fade" timeout={1000} in appear>
                <ShowPageCommentContainer
                  allComments={allComments}
                  authorTag={authorTag}
                  authorId={authorId}
                  authorName={authorName}
                  canCommentAnonymously={Boolean(canCommentAnonymously)}
                  canViewUserDetails={Boolean(canViewUserDetails)}
                  canWriteComments={Boolean(canWriteComments)}
                  comment={c}
                  commentableId={commentableId}
                  commentableType={commentableType as 'WhitePaper' | 'Idea'}
                  currentUserId={currentUserId}
                  highlightedCommentId={highlightedCommentId}
                  isAdmin={Boolean(isAdmin)}
                  isAllocator={Boolean(isAllocator)}
                  isAuthor={Boolean(isAuthor)}
                  isEmployed={Boolean(isEmployed)}
                  isIdeaAnonymous={isIdeaAnonymous}
                  isNonBuyside={isNonBuyside}
                  isIdeaClosed={false}
                  mentionables={mentionables}
                  profileImageUrl={profileImageUrl}
                />
              </CSSTransition>
            ))}
          </TransitionGroup>
        )}
      </div>
    </div>
  );
};

export default ShowPageComment;
